import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useState } from 'react';

import { MedicineApi } from '../../api';
import { Medicine } from '../../model';
import { formatMoney } from '../../utils';
import { Modal } from '../molecules';

const CHARACTERS = 'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЭЮЯ';

interface Props {
  isOpen: boolean;
  onClose(): void;
  onMedicineSelect(medicine: Medicine): void;
}

export const SelectMedicineModal: FC<Props> = ({
  isOpen,
  onClose,
  onMedicineSelect,
}) => {
  const [searchText, setSearchText] = useState('');
  const [activeCharacter, setActiveCharacter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const { data: medicines } = useQuery({
    keepPreviousData: true,
    queryFn: () =>
      MedicineApi.getAll(searchText, activeCharacter, currentPage, pageSize),
    queryKey: [
      MedicineApi.KEY,
      'getAll',
      searchText,
      activeCharacter,
      currentPage,
      pageSize,
    ],
  });

  const medicineList = medicines?.items || [];
  const totalMedicines = medicines?.total || 0;
  const totalPages = Math.ceil(totalMedicines / pageSize);

  // const filteredMedicines = medicineList.filter((medicine) => {
  //   const matchesSearchText = medicine.name.toLowerCase().includes(searchText.toLowerCase());
  //   const matchesActiveCharacter =
  //     !activeCharacter || medicine.name[0].toLowerCase() === activeCharacter.toLowerCase();
  //   return matchesSearchText && matchesActiveCharacter;
  // });

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleResetCharacter = useCallback(() => {
    setActiveCharacter('');
    setCurrentPage(1);
  }, []);

  const handleMedicineSelect = useCallback(
    (medicine: Medicine) => {
      onMedicineSelect(medicine);
      onClose();
    },
    [onClose, onMedicineSelect],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="rounded-[16px] bg-brand-6">
        <header className="px-[24px] pb-[24px] pt-[48px]">
          <input
            className="input--search input"
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Аспирин, Байер, грипп..."
            type="text"
            value={searchText}
          />
        </header>
        <div className="rounded-[16px] bg-white">
          <span className="block px-[24px] pt-[24px] text-[12px] text-brand-1/50">
            поиск препаратов по алфавиту
          </span>
          <div className="mt-[8px] flex flex-wrap items-center justify-between gap-y-[12px] px-[24px] pb-[24px]">
            <div className="flex flex-wrap gap-[8px]">
              {CHARACTERS.split('').map((character) => (
                <button
                  key={character}
                  type="button"
                  className={`border-b text-[18px] leading-none transition-all hover:text-brand-1 ${
                    activeCharacter === character
                      ? 'border-brand-1'
                      : 'border-transparent'
                  }`}
                  onClick={() => {
                    setActiveCharacter((prev) =>
                      prev === character ? '' : character,
                    );
                    setCurrentPage(1);
                  }}
                >
                  {character}
                </button>
              ))}
            </div>
            <button
              className={`button--secondary button transition-opacity ${
                activeCharacter === '' ? 'cursor-not-allowed opacity-50' : ''
              }`}
              onClick={handleResetCharacter}
              disabled={activeCharacter === ''}
            >
              Сбросить
            </button>
          </div>

          <div className="border-t border-dashed border-brand-1/50 px-[24px] pb-[24px] pt-[16px]">
            <span className="block">
              Найдено препаратов:&nbsp;{totalMedicines}
            </span>
            {medicineList.length > 0 && (
              <table className="mt-[24px] table">
                <thead>
                  <tr>
                    <th className="text-left">Название</th>
                    <th className="text-left">Форма выпуска</th>
                    <th className="text-right">Цена</th>
                  </tr>
                </thead>
                <tbody>
                  {medicineList.map((medicine) => (
                    <tr key={medicine.id}>
                      <td>
                        <div
                          className="flex cursor-pointer items-center gap-[20px] text-left font-bold"
                          onClick={() => handleMedicineSelect(medicine)}
                        >
                          {medicine.picture?.image && (
                            <img
                              alt={medicine.name}
                              height={44}
                              width={44}
                              src={medicine.picture.image}
                            />
                          )}
                          {medicine.name}
                        </div>
                      </td>
                      <td className="text-left">
                        {medicine.form_type?.value ?? '—'}&nbsp;массой&nbsp;
                        {medicine.dosage ?? '—'}&nbsp;
                        {medicine.dosage_type?.value ?? '—'}
                      </td>
                      <td className="text-right">
                        {formatMoney(medicine.medicine_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="mt-[24px] flex items-center justify-between">
              <div>
                <span>Страница {currentPage}</span>
              </div>
              <div className="flex gap-x-[8px]">
                <button
                  className="button--secondary button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Назад
                </button>
                <button
                  className="button--secondary button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Вперед
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
